// CLI v0.8 — `grid pull [<name>]`.
//
// Spec v17 §13.6: download the source bundle that was used for the last
// deploy. Useful when the local checkout is gone or out of sync with what's
// actually live. Server returns one signed GCS URL per service; we stream
// each tarball and extract under `./<name>/services/<svc>/`.
//
// Tarball extraction defends against path traversal — we reject any entry
// whose post-extraction path resolves outside its service directory.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { execSync } from 'child_process';
import { existsSync, mkdirSync, readdirSync, writeFileSync, rmSync } from 'fs';
import { join } from 'path';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { resolveEntityTarget } from '../services/resolve-entity-target.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

interface SourceResponse {
  entity_id: string;
  name: string;
  services: Record<string, string>;
  domain: string | null;
}

export interface RunPullOpts {
  client: IV2Client;
  name: string | undefined;
  cwd?: string;
  /** Allow extraction into a non-empty target dir. */
  force?: boolean;
  /** Suppress the human-readable hint (machine-readable output mode). */
  json?: boolean;
  /** Raw `-o` / `--output` value (BL621). Wins over `json` when present. */
  output?: string;
}

/**
 * Download the source bundle for an entity's last deploy and extract each
 * service tarball under `<targetDir>/services/<svc>/`.
 *
 * Shared by `pull` (downloads into `./<name>/`) and `clone` (downloads into
 * the clone target dir, then layers `cloudgrid.yaml` + `.cloudgrid/link.json`
 * on top). Returns the deploy domain so callers can print the public URL
 * without a second round-trip.
 *
 * Tarball extraction defends against path traversal — any entry resolving
 * outside its service directory aborts the download and removes the partial
 * service dir.
 */
export async function downloadEntitySource(opts: {
  client: IV2Client;
  entityId: string;
  targetDir: string;
  force: boolean;
  /** Suppress per-service progress lines (machine-readable output mode). */
  quiet?: boolean;
  /** ADR 036 V3 — fetch a specific version's source instead of HEAD. */
  version?: string;
}): Promise<{ services: string[]; domain: string | null }> {
  const resp = await opts.client.get<SourceResponse>(
    `/api/v2/runtimes/${encodeURIComponent(opts.entityId)}/source`,
    opts.version ? { version: opts.version } : undefined,
  );

  const services = resp.services ?? {};
  if (Object.keys(services).length === 0) {
    throw new V2ApiError(
      404, 'SOURCE_NOT_AVAILABLE',
      'No service sources are available for this entity. Redeploy to capture sources.',
    );
  }

  preflightTargetDir(opts.targetDir, services, opts.force);

  for (const [svcName, signedUrl] of Object.entries(services)) {
    if (!opts.quiet) process.stdout.write(`Downloading ${svcName}...\n`);
    const svcDir = join(opts.targetDir, 'services', svcName);
    mkdirSync(svcDir, { recursive: true });

    const downloaded = await fetch(signedUrl);
    if (!downloaded.ok) {
      throw new V2ApiError(
        downloaded.status, 'SOURCE_DOWNLOAD_FAILED',
        `Failed to download '${svcName}' (HTTP ${downloaded.status}).`,
      );
    }
    const buf = Buffer.from(await downloaded.arrayBuffer());
    const tarPath = join(svcDir, '.source.tar.gz');
    writeFileSync(tarPath, buf);
    try {
      // Use the lowest-common-denominator flags so we work on BSD tar
      // (macOS) and GNU tar (Linux). `find … -name "*..*"` below catches
      // any path-traversal entries that slip through.
      execSync(`tar -xzf .source.tar.gz -C .`, { cwd: svcDir });
    } finally {
      rmSync(tarPath, { force: true });
    }

    // Defense in depth — reject anything that escaped its dir.
    const traversal = execSync('find . -name "*..*"', { cwd: svcDir })
      .toString()
      .trim();
    if (traversal) {
      rmSync(svcDir, { recursive: true, force: true });
      throw new V2ApiError(
        400, 'SOURCE_TRAVERSAL_DETECTED',
        `Tarball for service '${svcName}' contained path traversal entries.`,
      );
    }
  }
  return { services: Object.keys(services), domain: resp.domain ?? null };
}

export async function runPull(opts: RunPullOpts): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  const cwd = opts.cwd ?? process.cwd();
  // ADR 036 V2: the source endpoint resolves the entity globally by
  // membership, so the link path skips the active-org pre-check.
  const target = await resolveEntityTarget(opts.client, opts.name, cwd, {
    skipLinkOrgCheck: true,
    bareSlugAcrossOrgs: true,
    verb: 'pull',
  });

  const targetDir = join(cwd, target.name);
  await downloadEntitySource({
    client: opts.client,
    entityId: target.entity_id,
    targetDir,
    force: opts.force ?? false,
    quiet: out.isStructured,
  });

  out.json(() => ({ entity: target.name, path: `./${target.name}` }));
  // download fetches bytes only — it does not link the folder to the entity, so
  // a follow-up `plug` here would fail. Point at `pickup` for the takeover path.
  out.human(
    () =>
      `Source for ${target.name} downloaded to ./${target.name}/.\n` +
      '\nSource downloaded. This folder is not linked to a live entity.\n' +
      `- To take ownership and plug: grid pickup ${target.name} instead\n` +
      '- To just inspect the code: nothing more to do\n',
  );
}

function preflightTargetDir(
  targetDir: string,
  services: Record<string, string>,
  force: boolean,
): void {
  if (!existsSync(targetDir)) {
    mkdirSync(targetDir, { recursive: true });
    mkdirSync(join(targetDir, 'services'), { recursive: true });
    return;
  }
  if (force) {
    mkdirSync(join(targetDir, 'services'), { recursive: true });
    return;
  }
  for (const svc of Object.keys(services)) {
    const svcDir = join(targetDir, 'services', svc);
    if (existsSync(svcDir) && readdirSync(svcDir).length > 0) {
      throw new V2ApiError(
        409, 'TARGET_NOT_EMPTY',
        `./${join(targetDir.split('/').pop() ?? '', 'services', svc)} already has files. Pass --force to overwrite.`,
      );
    }
  }
  mkdirSync(join(targetDir, 'services'), { recursive: true });
}

/**
 * Build the source-download command shared by `download` (and, before the
 * BL598 Phase 2 clean break, the deleted `pull`): bytes only, no
 * cloudgrid.yaml, no link. The `pull` verb itself is gone; this factory remains
 * because `download` is built from it. The optional `deprecationLine` parameter
 * is retained as a dormant seam — current callers (download) pass none.
 */
export function makePullLikeCommand(
  verb: string,
  deprecationLine?: string,
): CommanderCommand {
  const cmd = new Command(verb)
    .description('Download the source bundle that was used for the last deploy.')
    .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
    .option('--force', 'Overwrite existing files in the target service directories')
    .option('--json', 'Emit machine-readable output and suppress the hint');
  addOutputOption(cmd);
  return cmd.action(
    async (name: string | undefined, opts: { force?: boolean; json?: boolean; output?: string }) => {
      const structured = isStructuredOutput(opts);
      if (deprecationLine && !structured) process.stderr.write(deprecationLine + '\n');
      try {
        const client = await getV2Client();
        await runPull({ client, name, force: opts.force, json: opts.json, output: opts.output });
      } catch (err) {
        await handleV2Error(err, { json: structured });
      }
    },
  );
}
